import React, { useMemo, useCallback } from 'react';

import { useGetEventsInfinite } from '@vlabs/api/luna-api/client';
import type { Event } from '@vlabs/api/luna-api/model';
import { useIntersectionObserver } from '@vlabs/shared/hooks';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { useTranslation } from 'react-i18next';

import { EventHistoryCard } from './EventHistoryCard';
import st from './EventTopMatchHistory.module.sass';

const PAGE_SIZE = 10;

interface EventTopMatchHistoryProps {
  event?: Event;
  onEventSelect: (event: Event) => void;
}

export function EventTopMatchHistory({
  event = undefined,
  onEventSelect,
}: EventTopMatchHistoryProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const topMatchId = useMemo(
    () => event?.top_match?.face_id || event?.top_match?.event_id,
    [event],
  );

  const getEvents = useGetEventsInfinite(
    {
      top_similar_object_ids: topMatchId,
      page_size: PAGE_SIZE,
      page: 1,
    },
    {
      query: {
        enabled: !!topMatchId,
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
          const eventsCount = lastPage.events?.length ?? 0;

          return eventsCount < PAGE_SIZE
            ? undefined
            : allPages.length + 1;
        },
      },
    },
  );

  const eventHistory = useMemo(
    () => getEvents.data?.pages.flatMap((page) => page.events ?? []) ?? [],
    [getEvents.data],
  );

  const handleScroll = useCallback(() => {
    if (getEvents.hasNextPage && !getEvents.isFetchingNextPage) {
      getEvents.fetchNextPage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEvents.fetchNextPage, getEvents.hasNextPage, getEvents.isFetchingNextPage]);

  const ref = useIntersectionObserver(handleScroll);

  if (getEvents.isLoading) {
    return (
      <div className={st.EmptyHistoryPanel}>
        <Loader />
      </div>
    );
  }

  if (eventHistory.length === 0 || !topMatchId) {
    return (
      <div className={st.EmptyHistoryPanel}>
        {t('lunapoint:operator_panel.event_history.no_event_history')}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      {eventHistory.map((historyEvent) => (
        <EventHistoryCard
          event={historyEvent}
          isActive={historyEvent.event_id === event?.event_id}
          key={historyEvent.event_id}
          onClick={onEventSelect}
        />
      ))}

      {!!getEvents.hasNextPage && (
        <div
          className={st.Loader}
          ref={ref}
        >
          {!!getEvents.isFetchingNextPage && <Loader />}
        </div>
      )}
    </div>
  );
}
